// react
import { MouseEvent, useEffect, useRef, useState } from 'react';

// types
import { ActionButton } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink } from 'react-router-dom';

// mui
import {
	Button,
	ButtonGroup,
	ButtonGroupProps,
	ButtonProps,
	ClickAwayListener,
	Grow,
	ListItemIcon,
	MenuItem,
	MenuItemProps,
	MenuList,
	Paper,
	Popper,
} from '@mui/material';

// icons
import { ArrowDropDownOutlined as IconArrowDropDown } from '@mui/icons-material';

// props
export interface BtnSplitProps extends ButtonGroupProps {
	actions: ActionButton[];
	btnMain?: ActionButton;
	ButtonProps?: ButtonProps;
	MenuItemProps?: MenuItemProps;
}

export const BtnSplit = ({
	actions = [],
	btnMain,
	ButtonProps,
	MenuItemProps,
	size,
	...props
}: BtnSplitProps) => {
	// refs
	const refAnchor = useRef<HTMLDivElement | HTMLButtonElement>(null);

	// hooks
	const { t } = useTranslation();

	// state
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [isOpen, setIsOpen] = useState(false);

	const handleToggle = () => {
		setIsOpen((isOpenPrev) => !isOpenPrev);
	};

	const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
		if (btnMain) {
			handleToggle();
		} else {
			const action = actions[selectedIndex];
			if (action?.onClick) {
				action.onClick(e);
			}
		}
	};

	const handleMenuItemClick = (
		e: MouseEvent<HTMLElement, globalThis.MouseEvent>,
		action: ActionButton,
		index: number
	) => {
		if (action.onClick) {
			action.onClick(e);
		}
		if (!btnMain) {
			setSelectedIndex(index);
		}
		setIsOpen(false);
	};

	const handleClose = (e: Event) => {
		if (refAnchor.current?.contains(e.target as HTMLElement)) {
			return;
		}

		setIsOpen(false);
	};

	const menuItemProps: MenuItemProps = {
		...MenuItemProps,
	};

	if (size === 'small') {
		menuItemProps.sx = {
			...MenuItemProps?.sx,
			fontSize: '1.2rem',
			lineHeight: '2.2rem',
		};
	}

	const btnActive = btnMain || actions[selectedIndex];

	// reset selected index if it exceeds the current actions length
	useEffect(() => {
		if (selectedIndex >= actions.length) {
			setSelectedIndex(0);
		}
	}, [actions.length, selectedIndex]);

	return (
		<>
			<ButtonGroup
				aria-label={t('btnSplit')}
				disableElevation={true}
				size={size}
				variant="contained"
				{...props}
				ref={refAnchor as React.RefObject<HTMLDivElement>}>
				<Button {...btnActive} onClick={handleClick} />
				<Button
					aria-controls={isOpen ? 'split-button-menu' : undefined}
					aria-expanded={isOpen ? 'true' : undefined}
					aria-haspopup="menu"
					aria-label={t('selectAction')}
					className="icon"
					onClick={handleToggle}
					size="small"
					sx={{ flex: 1 }}>
					<IconArrowDropDown />
				</Button>
			</ButtonGroup>
			<Popper
				anchorEl={refAnchor.current}
				disablePortal={true}
				open={isOpen}
				placement="bottom-end"
				role={undefined}
				sx={{
					zIndex: 1,
				}}
				transition={true}>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'bottom right' : 'top right',
						}}>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id="split-button-menu" autoFocusItem>
									{actions.map((action, i) => {
										const menuItemContent = (
											<>
												{action.startIcon && <ListItemIcon>{action.startIcon}</ListItemIcon>}
												{action.children}
											</>
										);

										return (
											<MenuItem
												{...menuItemProps}
												key={`menu-item-${i}`}
												component={action.to ? RouterLink : 'li'}
												disabled={action.disabled}
												selected={i === selectedIndex}
												to={action.to}
												onClick={(e: MouseEvent<HTMLElement>) => handleMenuItemClick(e, action, i)}>
												{menuItemContent}
											</MenuItem>
										);
									})}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};
